/**
 * Builds the normalized dashboard inputs (WI 34293) from raw telemetry: bridge
 * context snapshots, quota rollups and hostMetrics samples on every host, plus
 * the Electron Control Center's local view (window.rynCC.attention()) when the
 * page runs inside it. Pure; no Angular, no I/O.
 */
import type {
  AccountRollup,
  ContextSnapshot,
} from '../models/flock-telemetry.models';
import { parseUtcMs } from '../models/flock-telemetry.utils';
import type {
  AccountSignal,
  AccountWindowSignal,
  HostSignal,
  SeatSignal,
  SeatState,
  WindowKey,
} from './attention.logic';
import type { SeatLogin } from './seat-login-alerts.logic';
import {
  HOST_SAMPLE_WINDOW_MS,
  SEAT_FRESH_MS,
  normalizeHost,
  seatsByHost,
} from './machines.logic';
import { PaceSampleMap, paceKey } from './pace.logic';

/** A seat snapshot this old no longer places a seat on its host. Longer than
 * the liveness window on purpose: a silent host must still show its seats. */
export const HOST_MEMBERSHIP_MS = 24 * 60 * 60_000;

// ── The Control Center's local view (lib/attention-data.js) ─────────────────

export interface CcAttentionSeat {
  instanceId: string;
  target: string | null;
  launcher: string | null;
  host: string;
  account: string | null;
  ctxPct: number | null;
  rate5h: number | null;
  /** Claude statusline resets_at: epoch seconds (or an ISO string). */
  resets5h: number | string | null;
  rate7d: number | null;
  resets7d: number | string | null;
  idleMinutes: number | null;
  state: SeatState;
  stateDetail: string | null;
  /** When a transcript verdict was written (ISO), else null. */
  stateAt?: string | null;
  paneReadable: boolean;
  /** WI 34592: the CC's seat login check; absent on an older CC. */
  login?: SeatLogin | null;
}

export interface CcAttention {
  at: string;
  host: string;
  user: string | null;
  version: { shortVersion?: string } | null;
  ccInstanceId: string | null;
  paneSupported: boolean;
  seats: CcAttentionSeat[];
  health: {
    host: string;
    at: string;
    cpuPct: number | null;
    memPct: number | null;
    diskPct: number | null;
    diskFreeBytes: number | null;
  } | null;
  errors: Record<string, string>;
}

/** One host metrics reading (bridge hostMetrics push or the CC's own probe). */
export interface HostReading {
  host: string;
  atMs: number;
  cpuPct: number | null;
  memPct: number | null;
  diskPct: number | null;
  diskFreeBytes: number | null;
}

/** A bridge seatStatus older than this says nothing about the seat now
 * (the column keeps its last write). Reporters post every 20 s to 2 min. */
export const SEAT_STATUS_FRESH_MS = 10 * 60_000;

/**
 * US 37271: the tile state a seat's own bridge seatStatus implies, for seats
 * no local Control Center can see (an external seat, or any seat viewed from
 * a machine without one). Fresh actionable statuses only; running, unknown,
 * missing or stale give null.
 */
export function bridgeSeatState(
  status: string | null | undefined,
  observedAt: string | null | undefined,
  nowMs: number
): SeatState {
  const at = observedAt ? parseUtcMs(observedAt) : null;
  if (at === null || nowMs - at > SEAT_STATUS_FRESH_MS) return null;
  switch (status) {
    case 'logged-out':
      return 'logged-out';
    case 'limit-reached':
      return 'limit-reached';
    case 'stuck-prompt':
      return 'prompt';
    default:
      return null;
  }
}

function alive(s: ContextSnapshot): boolean {
  return s.processState !== 'reaped' && s.processState !== 'orphaned';
}

// ── Seats ───────────────────────────────────────────────────────────────────

/**
 * Seats from fresh bridge context snapshots, with the CC's local seats laid
 * over them: the local statusline context is newer, and the CC reads a seat's
 * pane directly. A remote seat's state comes from its own reported seatStatus.
 */
export function buildSeatSignals(
  snapshots: ContextSnapshot[],
  cc: CcAttention | null,
  nowMs: number
): SeatSignal[] {
  const byId = new Map<string, SeatSignal>();
  for (const s of snapshots) {
    const observed = parseUtcMs(s.observedAt);
    if (!alive(s) || observed === null || nowMs - observed > SEAT_FRESH_MS)
      continue;
    byId.set(s.instanceId, {
      instanceId: s.instanceId,
      host: normalizeHost(s.reportingHost) || null,
      account: s.accountUpn,
      ctxPct: s.percentUsed,
      state: bridgeSeatState(s.seatStatus, s.seatStatusObservedAt, nowMs),
      stateDetail: null,
      idleMinutes: s.idleMinutes,
      local: false,
    });
  }
  for (const l of cc?.seats ?? []) {
    const prev = byId.get(l.instanceId);
    byId.set(l.instanceId, {
      instanceId: l.instanceId,
      host: normalizeHost(l.host) || prev?.host || null,
      account: l.account ?? prev?.account ?? null,
      ctxPct: l.ctxPct ?? prev?.ctxPct ?? null,
      state: l.state,
      stateDetail: l.stateDetail,
      idleMinutes: l.idleMinutes ?? prev?.idleMinutes ?? null,
      local: true,
      ...(l.login !== undefined ? { login: l.login } : {}),
    });
  }
  return [...byId.values()];
}

// ── Hosts ───────────────────────────────────────────────────────────────────

/**
 * Every machine that has seats (placement in the last 24h) or reports
 * metrics. Seat placement is sourced from `seatsByHost` (machines.logic),
 * shared with the machine tiles: `freshSeats` wins when any exist, else the
 * last-known `allSeats` carries the count forward with `seatsStale` set (WI
 * 34272 review, P1). A host must never read as zero seats just because the
 * render clock walked past SEAT_FRESH_MS since its last placement snapshot,
 * as long as it (or its metrics) are still being reported. Reporting age is
 * the newest of its metrics readings and its seat placement. CPU and memory
 * come from the newest reading; disk is the WORST reading in the minute
 * before it (two publishers report different volumes for one host today, WI
 * 34279, and the near-full one must not flicker out of sight).
 */
export function buildHostSignals(
  snapshots: ContextSnapshot[],
  readings: HostReading[],
  nowMs: number
): HostSignal[] {
  const eligible = snapshots.filter((s) => {
    const observed = parseUtcMs(s.observedAt);
    return (
      alive(s) && observed !== null && nowMs - observed <= HOST_MEMBERSHIP_MS
    );
  });
  const seatMap = seatsByHost(eligible, nowMs);

  const last = new Map<string, number>();
  const bump = (h: string, t: number) =>
    last.set(h, Math.max(last.get(h) ?? 0, t));
  for (const [host, info] of seatMap) bump(host, info.lastObservedAtMs);

  const byHost = new Map<string, HostReading[]>();
  for (const r of readings) {
    const host = normalizeHost(r.host);
    if (!host) continue;
    bump(host, r.atMs);
    byHost.set(host, [...(byHost.get(host) ?? []), r]);
  }

  const hosts = new Set<string>([...seatMap.keys(), ...byHost.keys()]);
  return [...hosts]
    .map((host) => {
      const info = seatMap.get(host);
      const freshCount = info?.freshSeats.length ?? 0;
      const seatIds =
        freshCount > 0 ? info!.freshSeats : (info?.allSeats ?? []);
      const seatsStale = !info || nowMs - info.lastObservedAtMs > SEAT_FRESH_MS;

      const rs = (byHost.get(host) ?? []).sort((a, b) => b.atMs - a.atMs);
      const newest = rs[0];
      const recent = rs.filter(
        (r) => newest && newest.atMs - r.atMs <= HOST_SAMPLE_WINDOW_MS
      );
      const worstDisk = recent.reduce<HostReading | null>(
        (w, r) =>
          r.diskPct !== null && (w === null || (w.diskPct ?? -1) < r.diskPct)
            ? r
            : w,
        null
      );
      return {
        host,
        seatIds: [...seatIds].sort(),
        seatsStale,
        seatsLastObservedAtMs: info?.lastObservedAtMs ?? null,
        lastReportAtMs: last.get(host) ?? null,
        cpuPct: newest?.cpuPct ?? null,
        memPct: newest?.memPct ?? null,
        diskPct: worstDisk?.diskPct ?? null,
        diskFreeBytes: worstDisk?.diskFreeBytes ?? null,
      };
    })
    .sort((a, b) => a.host.localeCompare(b.host));
}

/** The CC's own health probe as a host reading. */
export function ccHostReading(cc: CcAttention | null): HostReading | null {
  const h = cc?.health;
  const at = parseUtcMs(h?.at ?? null);
  if (!h || at === null) return null;
  return {
    host: h.host,
    atMs: at,
    cpuPct: h.cpuPct,
    memPct: h.memPct,
    diskPct: h.diskPct,
    diskFreeBytes: h.diskFreeBytes,
  };
}

// ── Accounts ────────────────────────────────────────────────────────────────

/** Statusline resets_at is epoch SECONDS; an ISO string is accepted too. */
export function resetToMs(v: number | string | null): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
  return parseUtcMs(v);
}

/** A reading the Anthropic API reported. A cap-divided token estimate is a
 * guess on both ends and is never shown as a live percent. */
function isMeasured(source: string | null): boolean {
  return source !== null && source !== 'estimate';
}

/**
 * Per-account 5h and 7d windows: the bridge rollup (measured rows only), with
 * a local seat's live statusline reading winning when the CC has one.
 */
export function buildAccountSignals(
  rollups: AccountRollup[],
  cc: CcAttention | null,
  seatsByAccount: Map<string, string[]>,
  samples: PaceSampleMap,
  nowMs: number
): AccountSignal[] {
  const acc = new Map<
    string,
    { upn: string; w: Map<WindowKey, AccountWindowSignal> }
  >();
  const put = (
    upn: string,
    window: WindowKey,
    percent: number,
    resetAtMs: number | null
  ) => {
    if (resetAtMs === null || resetAtMs <= nowMs) return;
    const k = upn.toLowerCase();
    const a = acc.get(k) ?? { upn, w: new Map() };
    a.w.set(window, {
      window,
      percent,
      resetAtMs,
      samples: samples.get(paceKey(upn, window)) ?? [],
    });
    acc.set(k, a);
  };
  for (const r of rollups) {
    if (isMeasured(r.fiveHour.source))
      put(
        r.accountUpn,
        '5h',
        r.fiveHour.percentUsed,
        parseUtcMs(r.fiveHour.effectiveResetAt)
      );
    if (isMeasured(r.weekly.source))
      put(
        r.accountUpn,
        '7d',
        r.weekly.percentUsed,
        parseUtcMs(r.weekly.effectiveResetAt)
      );
  }
  for (const s of cc?.seats ?? []) {
    if (!s.account) continue;
    if (s.rate5h !== null)
      put(s.account, '5h', s.rate5h, resetToMs(s.resets5h));
    if (s.rate7d !== null)
      put(s.account, '7d', s.rate7d, resetToMs(s.resets7d));
  }
  return [...acc.entries()].map(([k, a]) => ({
    accountUpn: a.upn,
    seatIds: seatsByAccount.get(k) ?? [],
    windows: (['5h', '7d'] as WindowKey[])
      .map((w) => a.w.get(w))
      .filter((w): w is AccountWindowSignal => !!w),
  }));
}
